import { ApartmentOutlined, HddOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { ReactElement, useEffect, useState } from 'react';
import CcxComponentProps from '../../../../core/CcxComponent';
import useContentInstanceSizes from '../../../../core/hooks/useContentInstanceSizes';
import DeploymentsItem from '../../../../types/DeploymentsItem';
import InstanceSize from '../../../../types/InstanceSize';
import Service from '../../../../types/Service';
import CcxLogo from '../../../ccx/common/CcxLogo';
import CcxIconClockCircle from '../../../ccx/icons/CcxIconClockCircle';
import CopyToClipboardText from '../../../CopyToClipboardText';
import styles from './ServiceInformation.module.less';
import useDatastoreServiceDsn from './useDatastoreServiceDsn';
import ServiceStatus from './ServiceStatus';
import { getHostnameOrIP } from '../../../../core/helpers';

interface Props extends CcxComponentProps {
    dataStore?: DeploymentsItem;
    service?: Service;
    type?: string;
    deploying?: boolean;
    copyButton?: boolean;
    copyDsn?: boolean;
    datetimeInfo?: boolean;
    instanceInfo?: boolean;
}

function ServiceInformation({
    dataStore,
    service,
    type = 'db',
    testId = 'ServiceInformation',
    deploying = false,
    copyButton = true,
    copyDsn = false,
    datetimeInfo = true,
    instanceInfo = true,
}: Props): ReactElement {
    const { instanceSizes } = useContentInstanceSizes(
        service?.getCloudProvider()
    );
    const [instance, setInstance] = useState<InstanceSize>();
    const { dsn } = useDatastoreServiceDsn({ dataStore, service, type });

    useEffect(() => {
        if (instanceSizes && service) {
            const instances = instanceSizes.find(
                (i) => i.region === service.getRegionCode()
            );

            if (instances) {
                setInstance(
                    instances.development.find(
                        (instanceItem) =>
                            instanceItem.type === service.getInstanceType()
                    )
                );
            }
        }
    }, [instanceSizes, service]);

    const dbInfo = dataStore && (
        <div className={styles.ServiceInformationServiceInfo}>
            <CcxLogo logo={dataStore.getDbIconName()} size="xsmall" />
            <div>{dataStore.getDbVendorName()}</div>
            <div className={styles.DatabaseInformationVersion}>
                {dataStore.db.dbVersion}
            </div>
        </div>
    );

    const lbInfo = service && (
        <div className={styles.ServiceInformationServiceInfo}>
            <ApartmentOutlined />
            <div>{service.getElegantNodeType()}</div>
            <div className={styles.DatabaseInformationVersion}>
                {service.getNodeVersion()}
            </div>
        </div>
    );

    const fqdn = type === 'db' ? service?.getFQDN() : service?.getFQDN(true);

    const ipSection = service && (
        <>
            {deploying ? (
                <div className={styles.ServiceInformationMiddle}>
                    ---.---.---.---
                </div>
            ) : (
                <div className={styles.ServiceInformationMiddle}>
                    {fqdn &&
                        (copyButton ? (
                            <CopyToClipboardText
                                text={fqdn}
                                preview={getHostnameOrIP(fqdn)}
                                tooltip={fqdn}
                                hoverable={false}
                                nowrap={true}
                            />
                        ) : (
                            getHostnameOrIP(fqdn)
                        ))}
                </div>
            )}
        </>
    );

    const instanceTooltip = (
        <div>
            {instance && (
                <div>
                    Instances: {`${instance.cpu}vCPU, ${instance.ram} GB RAM`}
                </div>
            )}
            {service?.getDiskType() ? (
                <div>Storage: {service?.getDiskType()}</div>
            ) : null}
            {dataStore?.volumeSize ? (
                <div>Storage size: {dataStore?.volumeSize} GB</div>
            ) : null}
        </div>
    );

    const instanceInfoTooltip = instanceInfo && service && instance && (
        <Tooltip placement="left" title={instanceTooltip}>
            <div className={styles.ServiceInformationBottomSection}>
                <HddOutlined /> {instance.name}{' '}
                {dataStore?.volumeSize ? `, ${dataStore?.volumeSize} GB` : ''}
            </div>
        </Tooltip>
    );

    const datetimeInfoTooltip = datetimeInfo && service && !deploying && (
        <Tooltip
            title={
                <div>
                    Created: <br />
                    {`${service.getFullCreatedAt()} UTC`}
                </div>
            }
            placement="left"
        >
            <div className={styles.ServiceInformationBottomSection}>
                <CcxIconClockCircle /> {service.getFriendlyCreatedAt()}
            </div>
        </Tooltip>
    );

    const copyDsnSection = copyDsn && dsn && (
        <CopyToClipboardText
            text={dsn}
            tooltip={dsn}
            buttonProps={{
                children: <span>Copy DSN</span>,
                size: 'small',
                className: styles.ServiceInformationDsnBottom,
            }}
            hoverable={false}
            nowrap={true}
            showPreview={false}
            successMessage={'DSN copied to clipboard'}
        />
    );

    const availabilityZone = service?.getAvailabilityZone() && (
        <Tooltip title="Availability Zone" placement="left">
            <div className={styles.ServiceInformationBottomSection}>
                <ApartmentOutlined /> AZ: {service?.getAvailabilityZone()}
            </div>
        </Tooltip>
    );

    const serviceStatus = (
        <div className={styles.ServiceInformationServiceStatus}>
            <ServiceStatus
                service={service}
                testId={`${testId}${service?.getServiceUuid()}ServiceStatus`}
                updating={false}
            />
        </div>
    );

    return (
        <div className={styles.ServiceInformation} data-testid={testId}>
            <div className={styles.ServiceInformationStatusDot}>
                {serviceStatus}
                {ipSection}
            </div>
            {type === 'db' ? dbInfo : lbInfo}
            {instanceInfoTooltip}
            {availabilityZone}
            {datetimeInfoTooltip}
            {copyDsnSection}
        </div>
    );
}

export default ServiceInformation;
